import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, Circle, Building, Users, Wallet, Calendar, 
  ClipboardCheck, Settings, ChevronRight, Sparkles
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Progress Indicator component - Shows setup completion percentage
 * Displays on dashboard to guide new users through setup
 */
const ProgressIndicator = ({ token, variant = 'full' }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({
    buildings: 0,
    coproprietaires: 0,
    exercices: 0,
    assemblees: 0,
    visites: 0,
    equipe: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const [buildingsRes, coproRes, exercicesRes, assembleesRes, visitesRes, equipeRes] = await Promise.all([
          axios.get(`${API}/buildings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/coproprietaires`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/comptabilite/exercices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/assemblees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/visites`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API}/equipe`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);

        setProgress({
          buildings: Array.isArray(buildingsRes.data) ? buildingsRes.data.length : 0,
          coproprietaires: Array.isArray(coproRes.data) ? coproRes.data.length : 0,
          exercices: Array.isArray(exercicesRes.data) ? exercicesRes.data.length : 0,
          assemblees: Array.isArray(assembleesRes.data) ? assembleesRes.data.length : 0,
          visites: Array.isArray(visitesRes.data) ? visitesRes.data.length : 0,
          equipe: Array.isArray(equipeRes.data) ? equipeRes.data.length : 0
        });
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchProgress();
  }, [token]);

  const steps = [
    {
      id: 'buildings',
      label: 'Créer un immeuble',
      description: 'Ajoutez votre première copropriété',
      icon: Building,
      isComplete: progress.buildings > 0,
      path: '/immeubles',
      color: 'blue'
    },
    {
      id: 'coproprietaires',
      label: 'Ajouter des coproprietaires',
      description: 'Enregistrez les propriétaires',
      icon: Users,
      isComplete: progress.coproprietaires > 0,
      path: '/coproprietaires',
      color: 'emerald'
    },
    {
      id: 'exercices',
      label: 'Créer un exercice comptable',
      description: 'Configurez la comptabilité',
      icon: Wallet,
      isComplete: progress.exercices > 0,
      path: '/comptabilite',
      color: 'purple'
    },
    {
      id: 'assemblees',
      label: 'Planifier une AG',
      description: 'Préparez votre première assemblée',
      icon: Calendar,
      isComplete: progress.assemblees > 0,
      path: '/assemblees',
      color: 'amber'
    }
  ];

  const completedSteps = steps.filter(s => s.isComplete).length;
  const progressPercent = Math.round((completedSteps / steps.length) * 100);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="h-2 bg-slate-100 rounded-full mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-10 bg-slate-50 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  // Compact variant for sidebar or small spaces
  if (variant === 'compact') {
    return (
      <div 
        className="bg-gradient-to-r from-[#00D3A8]/10 to-[#00B892]/10 rounded-xl p-4 border border-[#00D3A8]/20 cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate('/immeubles')}
        data-testid="progress-indicator-compact"
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Configuration</span>
          <span className="text-sm font-bold text-[#00D3A8]">{progressPercent}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-[#00D3A8] to-[#00B892] h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {completedSteps}/{steps.length} étapes complétées
        </p>
      </div>
    );
  }

  // Full variant for dashboard
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="progress-indicator">
      {/* Header */}
      <div className="p-4 md:p-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-[#00D3A8] to-[#00B892] rounded-lg">
              <Sparkles className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Configuration du compte
              </h3>
              <p className="text-xs text-slate-500">
                {progressPercent === 100 ? 'Félicitations ! Configuration terminée' : 'Suivez les étapes pour démarrer'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-[#00D3A8]">{progressPercent}%</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="w-full bg-slate-100 rounded-full h-2.5">
            <div 
              className="bg-gradient-to-r from-[#00D3A8] to-[#00B892] h-2.5 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="p-3 md:p-4">
        <div className="space-y-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const colorClasses = {
              blue: 'bg-blue-50 text-blue-600',
              emerald: 'bg-emerald-50 text-emerald-600',
              purple: 'bg-purple-50 text-purple-600',
              amber: 'bg-amber-50 text-amber-600'
            };

            return (
              <button
                key={step.id}
                onClick={() => navigate(step.path)}
                disabled={step.isComplete}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                  step.isComplete 
                    ? 'bg-emerald-50/50 cursor-default' 
                    : 'hover:bg-slate-50 cursor-pointer group'
                }`}
                data-testid={`progress-step-${step.id}`}
              >
                <div className="flex items-center space-x-3">
                  {step.isComplete ? (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="text-emerald-600" size={18} />
                    </div>
                  ) : (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClasses[step.color]}`}>
                      <Icon size={16} />
                    </div>
                  )}
                  <div className="text-left">
                    <p className={`text-sm font-medium ${step.isComplete ? 'text-emerald-700' : 'text-slate-700'}`}>
                      {step.label}
                    </p>
                    <p className="text-xs text-slate-400">{step.description}</p>
                  </div>
                </div>
                {!step.isComplete && (
                  <ChevronRight className="text-slate-300 group-hover:text-[#00D3A8] transition-colors" size={18} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer message */}
      {progressPercent === 100 && (
        <div className="px-4 pb-4">
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <p className="text-xs text-emerald-700 text-center">
              Votre compte est entièrement configuré. Explorez les autres fonctionnalités !
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator;
